import * as React from "react";
import z from "zod";
import { t } from "i18next";
import { Button, Form, Select, Space } from "antd";
import { observer } from "mobx-react-lite";
import { cvtError, onError } from "src/common/onError";
import { AttrTypeName } from "src/types/AttrType";
import { DrawerPlm } from "src/components/DrawerPlm";
import {
  CheckCircleFilled,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { RemoteData } from "src/common/RemoteData";
import { ZIdName } from "src/types/ZIdName";
import { LoaderBox } from "src/components/LoaderBox";
import { PropsCell } from "src/components/Sheet2/PropsCell2";
import { KeyDef } from "src/components/Sheet2/isSymbolKey";
import { loadBom3MaterialSizes } from "../../../apiBom3";
import { Bom3PositionStore } from "../../Bom3PositionsStore";
import { ZBom3PosColumn, ZBom3PosRow } from "../../ZBom3Position";
import styles from "./SizeVarChange.module.less";
import { Cell2WithModal } from "../Cell2WithModal";

type PropsSizeVarChange = {
  cell: PropsCell;
  col: ZBom3PosColumn;
  row: ZBom3PosRow;
  store: Bom3PositionStore;
  onFocus: (e: FocusEvent) => void;
};

const zMatSizeName = z.object({
  sizeName: z.string(),
  materialSizeId: z.string().nullable(),
});
type ZMatSizeName = z.infer<typeof zMatSizeName>;

const unpackSizeVars = (rawValue: string): ZMatSizeName[] => {
  const srcJson = JSON.parse(rawValue);
  return zMatSizeName.array().parse(srcJson);
};
const packSizeVarValues = (
  values: Record<string, unknown>,
  srcValue: ZMatSizeName[],
): string => {
  const dstValue: ZMatSizeName[] = srcValue.map(({ sizeName }) => {
    const newSize = values[sizeName];
    const materialSizeId = typeof newSize === "string" ? newSize : null;
    return { sizeName, materialSizeId };
  });
  return JSON.stringify(dstValue);
};

export const makeEmptySizeVar = (
  col: ZBom3PosColumn,
  row: ZBom3PosRow,
): string => {
  try {
    const srcValueRaw = row.attributes[col.id]?.value ?? "";
    const srcList = unpackSizeVars(srcValueRaw);
    const dstList: ZMatSizeName[] = srcList.map(({ sizeName }) => ({
      sizeName,
      materialSizeId: null,
    }));
    return JSON.stringify(dstList);
  } catch (_) {
    return "";
  }
};

export const SizeVarChange: React.FC<PropsSizeVarChange> = observer((props) => {
  const { cell, col, row, store, onFocus } = props;
  const readOnly = !store.possibleUpdate;
  let btnIcon: React.ReactNode = null;
  let btnTitle: string | undefined;
  let srcValue: ZMatSizeName[] | undefined;
  const srcValueRaw = row.attributes[col.id]?.value ?? "";
  try {
    srcValue = unpackSizeVars(srcValueRaw);
    if (srcValue.some(({ materialSizeId }) => !!materialSizeId)) {
      btnIcon = <CheckCircleFilled />;
    } else {
      btnIcon = <EditOutlined />;
    }
  } catch (error) {
    btnIcon = <WarningOutlined />;
    btnTitle = `Некорректное значение поля ${col.displayName}`;
  }
  const [form] = Form.useForm();
  const [matSizes, setMatSizes] = React.useState<RemoteData<ZIdName[]>>({
    status: "none",
  });
  const open = matSizes.status !== "none";

  React.useEffect(() => {
    form.resetFields();
    srcValue?.forEach(({ sizeName, materialSizeId }) =>
      form.setFieldValue(sizeName, materialSizeId),
    );
  }, [open]);

  const close = () => setMatSizes({ status: "none" });
  const onClick = () => {
    setMatSizes({ status: "wait" });
    loadMatSizes(store, row)
      .then((result) => setMatSizes({ status: "ready", result }))
      .catch((e) => setMatSizes({ status: "error", error: cvtError(e) }));
  };
  const ref = React.useRef<HTMLElement>(null);

  const saveValue = (values: Record<string, unknown>) => {
    const dstValue = packSizeVarValues(values, srcValue ?? []);
    store.directChangeValue(cell.cellKey, dstValue).catch(onError);
    close();
  };

  const isKeySpecial = (e: KeyDef): boolean => {
    if (e.code === "Enter") {
      return true;
    }
    return false;
  };

  return (
    <Cell2WithModal
      btnRef={ref}
      cell={cell}
      isKeySpecial={isKeySpecial}
      onFocus={onFocus}
    >
      <Button
        ref={ref}
        style={{ width: "100%" }}
        onClick={onClick}
        disabled={!srcValue}
        icon={btnIcon}
        title={btnTitle}
      />
      <DrawerPlm
        open={open}
        onClose={close}
        title="Size"
        width={800}
        extra={
          <Space>
            <Button
              disabled={matSizes.status !== "ready" || readOnly}
              onClick={() => {
                form.resetFields();
              }}
            >
              {t("Reset")}
            </Button>
            <Button
              type="primary"
              disabled={matSizes.status !== "ready" || readOnly}
              onClick={() => form.submit()}
            >
              {t("Apply")}
            </Button>
          </Space>
        }
      >
        <Form form={form} onFinish={saveValue} disabled={readOnly}>
          <LoaderBox
            remoteData={matSizes}
            drawReady={(list: ZIdName[]) => (
              <FormContent options={list} srcValue={srcValue ?? []} />
            )}
          />
        </Form>
      </DrawerPlm>
    </Cell2WithModal>
  );
});

type PropsFormContent = {
  options: ZIdName[];
  srcValue: ZMatSizeName[];
};

const FormContent: React.FC<PropsFormContent> = (props) => {
  const { options, srcValue } = props;

  return (
    <div className={styles.formContent}>
      {srcValue.map(({ sizeName }) => (
        <React.Fragment key={sizeName}>
          <div>{sizeName}</div>
          <div>
            <Form.Item name={sizeName}>
              <Select
                options={options}
                fieldNames={{ label: "name", value: "id" }}
                allowClear
              />
            </Form.Item>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const loadMatSizes = async (
  store: Bom3PositionStore,
  row: ZBom3PosRow,
): Promise<ZIdName[]> => {
  const { data, typesMap, bom } = store;
  const typeName = (type: number): string => typesMap[type] ?? String(type);
  if (data.status !== "ready") {
    throw Error("Нет данных о позициях");
  }
  // Предполагается, что тип object может быть только у колонки с материалами.
  // Если это предположение станет неверным, то останется только один путь - определять отдельный behavior для материала
  // Это касается и функции createBom3Columns
  const matCol = data.result.schema.attributes.find(
    ({ type }) => typeName(type) === AttrTypeName.object,
  );
  if (!matCol) throw Error("Не найден атрибут материала в схеме");
  const materialId = row.attributes[matCol.id]?.value;
  if (!materialId) throw Error("Отсутствует описание материала в строке");
  return loadBom3MaterialSizes(bom.id, materialId);
};
